import React, { Component, Fragment } from 'react';
import { Button, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';

import globalTranslations from '../../../i18n/globalTranslations';
import generatePath from '../../../util/generatePath';
import Paths from '../../../types/Paths';
import OrderTotalClass from '../../../types/OrderTotalClass';
import RouteService from '../../../services/RouteService';
import ProductTypeClass from '../../../types/ProductTypeClass';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import OrderStatusLabel from '../OrderStatusLabel';
import Price from '../../product/Price';
import AccountStore from '../../../store/AccountStore';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import RecurringOrder from '../../../models/RecurringOrder';

const MAX_PRODUCT_IMAGE_COUNT = 6;

@observer
export class RecurringOrderPreview extends Component {
  getTotalPrice = () => {
    const { order } = this.props;
    const priceTotal = order.totals.find(
      (total) => total.class === OrderTotalClass.TOTAL
    );

    return priceTotal.value || null;
  };

  getTotalTaxlessPrice = () => {
    const { order } = this.props;
    const priceTotal = order.totals.find(
      (total) => total.class === OrderTotalClass.TAXLESS_TOTAL
    );

    return priceTotal.value || null;
  };

  getOrderStatus = () => {
    const { order } = this.props;

    if (!order.recurring_status) {
      return null;
    }
    const onHoldUntil = order.on_hold_until;

    return (
      <Fragment>
        <OrderStatusLabel status={order.recurring_status} />
        {onHoldUntil && (
          <div className="RecurringOrderPreview__order-info-hold">
            <FormattedMessage
              id="recurringOrder.onHoldSentence"
              defaultMessage="On hold until: {untilDate}"
              values={{
                untilDate: this.getOrderDate(onHoldUntil)
              }}
            />
          </div>
        )}
      </Fragment>
    );
  };

  getProductCount = () => {
    const physicalProducts = this.getPhysicalProducts();
    if (!physicalProducts) {
      return null;
    }

    return (
      <FormattedMessage
        {...globalTranslations.productCount}
        values={{ count: physicalProducts.length }}
      />
    );
  };

  getOrderInfo = (order) => {
    const allowUnderDeliveryValue = order.allow_under_delivery ? (
      <FormattedMessage {...globalTranslations.yesTitle} />
    ) : (
      <FormattedMessage {...globalTranslations.noTitle} />
    );
    const allowShippingMethodChangeValue = order.allow_shipping_method_change ? (
      <FormattedMessage {...globalTranslations.yesTitle} />
    ) : (
      <FormattedMessage {...globalTranslations.noTitle} />
    );

    return (
      order && (
        <Fragment>
          <div className="RecurringOrderPreview__order-info-partial-delivery">
            <FormattedMessage
              id="recurringOrder.allowUnderDeliveryValue"
              defaultMessage="Allow partial delivery when all products are not available: {allowUnder}"
              values={{
                allowUnder: <b>{allowUnderDeliveryValue}</b>
              }}
            />
          </div>
          <div className="RecurringOrderPreview__order-info-shipping-change">
            <FormattedMessage
              id="recurringOrder.allowShippingChangeValue"
              defaultMessage="Allow automatic change of shipping method when current method is not possible: {allowUnder}"
              values={{
                allowUnder: <b>{allowShippingMethodChangeValue}</b>
              }}
            />
          </div>
        </Fragment>
      )
    );
  };

  getPhysicalProducts = () => {
    return this.props.order.products.filter(
      (orderProduct) => orderProduct.type === ProductTypeClass.PHYSICAL
    );
  };

  getProductImages = () => {
    const physicalProducts = this.getPhysicalProducts();

    if (!physicalProducts || physicalProducts.length === 0) {
      return null;
    }

    return physicalProducts
      .filter((orderProduct, index) => index <= MAX_PRODUCT_IMAGE_COUNT)
      .map((orderProduct, index) => {
        const productImageModel = orderProduct.image_url
          ? ProductImageModel.create({
              id: index,
              product_id: orderProduct.id,
              sizes: {
                small: orderProduct.image_url
              },
              description: ''
            })
          : null;

        const productImage = (
          <ProductImage
            product={orderProduct}
            productImage={productImageModel}
            size="small"
          />
        );

        const product = orderProduct.product_url ? (
          <MaybeExternalLink to={orderProduct.product_url}>
            {productImage}
          </MaybeExternalLink>
        ) : (
          productImage
        );

        return (
          <div
            key={orderProduct.id}
            className="RecurringOrderPreview__products-image"
          >
            {product}
          </div>
        );
      });
  };

  getPath = (path) => {
    const { order, routeService } = this.props;
    return routeService.getPath(generatePath(path, { id: order.id }));
  };

  renderOrderModifyButton = () => {
    return (
      <Link to={this.getPath(Paths.MyAccountRecurringOrder)}>
        <Button className="RecurringOrderPreview__button-view" color="primary">
          <FormattedMessage {...globalTranslations.toOrder} />
          {' / '}
          <FormattedMessage {...globalTranslations.editTitle} />
        </Button>
      </Link>
    );
  };

  renderOrderCancelButton = (id) => {
    const { onRemoveClick } = this.props;

    return (
      <Button
        className="RecurringOrderPreview__button-cancel"
        color="danger"
        onClick={() =>
          window.confirm(
            this.props.intl.formatMessage(globalTranslations.confirmRemoveTitle)
          ) && onRemoveClick(id)
        }
      >
        <FormattedMessage
          id="myAccount.cancelOrder"
          defaultMessage="Cancel order"
        />
      </Button>
    );
  };

  getOrderDate = (dateString) => {
    return DateTime.fromISO(dateString).toLocaleString();
  };

  render() {
    const { order, accountStore } = this.props;
    if (!order) {
      return null;
    }

    const title = order.title || order.autogenerated_title;

    return (
      <div className="RecurringOrderPreview">
        <Row className="RecurringOrderPreview__row">
          <Col md="5" sm="12">
            <div className="RecurringOrderPreview__order-information">
              <div className="RecurringOrderPreview__link">{title}</div>
              <div className="RecurringOrderPreview__products-images">
                {this.getProductImages()}
              </div>
              <div className="RecurringOrderPreview__order-info">
                {this.getOrderInfo(order)}
              </div>
            </div>
          </Col>
          <Col md="2" sm="12">
            <div className="RecurringOrderPreview__status">
              {this.getOrderStatus()}
            </div>
          </Col>
          <Col md="2" sm="12">
            <div className="RecurringOrderPreview__price">
              <Price price={this.getTotalPrice()} />
            </div>
            {accountStore.isRetailer && (
              <div className="RecurringOrderPreview__price">
                <Price price={this.getTotalTaxlessPrice()} />{' '}
                <FormattedMessage {...globalTranslations.taxExcluded} />
              </div>
            )}
          </Col>
          <Col md="3" sm="12">
            <div className="RecurringOrderPreview__buttons">
              {this.renderOrderModifyButton()}
              {this.renderOrderCancelButton(order.id)}
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

RecurringOrderPreview.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  order: modelOf(RecurringOrder),
  intl: intlShape.isRequired
};

export default inject('routeService', 'accountStore')(
  injectIntl(RecurringOrderPreview)
);



// WEBPACK FOOTER //
// ./src/components/order/RecurringOrderPreview/index.jsx